import { useEffect, useState } from "react";

// Controls that have their own native or ARIA Space activation. Space must reach
// them so it can trigger a click — the hold-Space-to-pan handler must not call
// preventDefault when one of these (or a descendant) is focused. `tabindex="-1"`
// is excluded: it marks programmatic-focus-only elements (e.g. the canvas), not
// user-activatable controls.
const INTERACTIVE_CONTROL_SELECTOR =
  'button, a[href], select, [role="button"], [role="link"], [role="menuitem"], [tabindex]:not([tabindex="-1"])';

function shouldIgnoreSpace(e: KeyboardEvent): boolean {
  const { target } = e;
  // Element (not HTMLElement) so SVG-based controls (e.g. <svg role="button">)
  // are covered too; window/document targets fall through to pan.
  if (!(target instanceof Element)) {
    return false;
  }

  if (
    target instanceof HTMLElement &&
    (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
  ) {
    return true;
  }

  return target.closest(INTERACTIVE_CONTROL_SELECTOR) !== null;
}

export function useSpaceKey() {
  const [isSpaceHeld, setIsSpaceHeld] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Space" && !e.repeat && !shouldIgnoreSpace(e)) {
        e.preventDefault();
        setIsSpaceHeld(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code !== "Space") {
        return;
      }
      // Only suppress the page-scroll default for the pan gesture; never for an
      // interactive control (its Space click fires on keyup).
      if (!shouldIgnoreSpace(e)) {
        e.preventDefault();
      }
      // Always release the pan hold when Space is released, regardless of what is
      // focused — otherwise focus moving onto a control mid-hold leaves the canvas
      // stuck in pan mode (readonly/non-draggable) until reload.
      setIsSpaceHeld(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  return isSpaceHeld;
}
